import { useAuth } from '@/providers/AuthProvider'
import type { AuthSession } from '@/lib/authToken'

/** Permission keys mirrored from the API catalog (IMPLEMENTATION_PLAN §7). */
export const PERMISSIONS = {
  ticketsRead: 'tickets.read',
  ticketsCreate: 'tickets.create',
  ticketsUpdate: 'tickets.update',
  ticketsAssign: 'tickets.assign',
  ticketsResolve: 'tickets.resolve',
  ticketsReopen: 'tickets.reopen',
  ticketsLinkRecord: 'tickets.link_record',
  ticketsEscalateDeviation: 'tickets.escalate_deviation',
  ticketsExport: 'tickets.export',
  ticketsExportInternal: 'tickets.export_internal',
  messagesReply: 'messages.reply',
  notesRead: 'notes.read',
  notesWrite: 'notes.write',
  attachmentsUpload: 'attachments.upload',
  attachmentsRead: 'attachments.read',
  auditRead: 'audit.read',
  csatSubmit: 'csat.submit',
  analyticsRead: 'analytics.read',
  analyticsExport: 'analytics.export',
  slaManage: 'sla.manage',
  routingManage: 'routing.manage',
  customersManage: 'customers.manage',
  contactsManage: 'contacts.manage',
  productsManage: 'products.manage',
  teamsManage: 'teams.manage',
  viewsManage: 'views.manage',
  usersManage: 'users.manage',
  rolesManage: 'roles.manage',
  integrationsManage: 'integrations.manage',
} as const

export type PermissionKey = (typeof PERMISSIONS)[keyof typeof PERMISSIONS]

/** Pure check: superadmin holds everything; nobody signed in holds nothing. */
export function hasPermission(user: AuthSession | null, key: string): boolean {
  if (!user) return false
  if (user.isSuperAdmin) return true
  return user.permissions.includes(key)
}

/** True when the signed-in principal holds `key` (superadmin → always true). */
export function usePermission(key: PermissionKey): boolean {
  const { user } = useAuth()
  return hasPermission(user, key)
}
